import { type ReactElement } from 'react';
import { Box, Group, Text } from '@mantine/core';
import MegaGrid, { useMegaGridState, type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import { formatUsd } from '@/shared/analytics/MetricFormatting';
import { adoptionBand } from './AdoptionBands';
import { ADOPTION_DIMENSIONS, ADOPTION_GROUP_PILLS } from './AdoptionDimensions';
import { adoptionSavedViews } from './AdoptionSavedViews';
import type { AdoptionBreakdownRow, AdoptionScope } from './AdoptionTypes';

interface AdoptionDriverGridProps {
    rows: AdoptionBreakdownRow[];
    /** Called with the full drill path (org → … → node) when a group or person row is clicked. */
    onDrill: (path: AdoptionScope[]) => void;
}

/** Spend-weighted average of a numeric field across a group's leaf rows. */
function weighted(rows: AdoptionBreakdownRow[], select: (r: AdoptionBreakdownRow) => number): number {
    const total = rows.reduce((sum, r) => sum + r.spend, 0) || 1;
    return Math.round(rows.reduce((sum, r) => sum + select(r) * r.spend, 0) / total);
}

function ScoreCell({ value }: { value: number }): ReactElement {
    return (
        <Group gap={7} justify="flex-end" wrap="nowrap">
            <Text fw={700} ff="monospace" style={{ fontSize: 13 }}>
                {value}
            </Text>
            <Box style={{ width: 8, height: 8, borderRadius: '50%', background: adoptionBand(value).color }} />
        </Group>
    );
}

type NumericKey = 'volume' | 'frequency' | 'complexity' | 'consistency';

function numColumn(key: NumericKey, header: string): MegaGridColumn<AdoptionBreakdownRow> {
    return {
        key,
        header,
        align: 'right',
        width: 104,
        noGroup: true,
        sortValue: (r) => r[key],
        render: (r) => <Text style={{ fontSize: 13 }}>{r[key]}</Text>,
        groupRender: (rows) => <Text style={{ fontSize: 13 }}>{weighted(rows, (r) => r[key])}</Text>,
    };
}

const DIMENSION_COLUMNS: MegaGridColumn<AdoptionBreakdownRow>[] = ADOPTION_DIMENSIONS.map((dim) => ({
    key: dim.columnKey,
    header: dim.label,
    defaultHidden: true,
    sortValue: (r) => r.dims[dim.columnKey] ?? '',
    groupValue: (r) => r.dims[dim.columnKey] ?? '—',
    render: (r) => r.dims[dim.columnKey] ?? '—',
}));

const COLUMNS: MegaGridColumn<AdoptionBreakdownRow>[] = [
    { key: 'label', header: 'Group', minWidth: 220, noGroup: true, sortValue: (r) => r.label, render: (r) => <Text fw={600} style={{ fontSize: 13 }}>{r.label}</Text> },
    {
        key: 'adoption',
        header: 'Adoption',
        align: 'right',
        width: 110,
        noGroup: true,
        sortValue: (r) => r.adoption,
        render: (r) => <ScoreCell value={r.adoption} />,
        groupRender: (rows) => <ScoreCell value={weighted(rows, (r) => r.adoption)} />,
    },
    numColumn('volume', 'Activity'),
    numColumn('frequency', 'Frequency'),
    numColumn('complexity', 'Complexity'),
    numColumn('consistency', 'Consistency'),
    {
        key: 'reach',
        header: 'Reach',
        align: 'right',
        width: 96,
        noGroup: true,
        sortValue: (r) => r.reach,
        render: (r) => <Text style={{ fontSize: 13 }}>{Math.round(r.reach * 100)}%</Text>,
        groupRender: (rows) => <Text style={{ fontSize: 13 }}>{Math.round(weighted(rows, (r) => r.reach * 100))}%</Text>,
    },
    {
        key: 'spend',
        header: 'Spend / mo',
        align: 'right',
        width: 110,
        noGroup: true,
        sortValue: (r) => r.spend,
        render: (r) => <Text ff="monospace" style={{ fontSize: 13 }}>{formatUsd(r.spend)}</Text>,
        groupRender: (rows) => <Text ff="monospace" style={{ fontSize: 13 }}>{formatUsd(rows.reduce((sum, r) => sum + r.spend, 0))}</Text>,
    },
    ...DIMENSION_COLUMNS,
];

/** Pinned Power/Idle presets, built once from the column set (see {@link adoptionSavedViews}). */
const ADOPTION_SEED_VIEWS = adoptionSavedViews(COLUMNS);

/**
 * The scored adoption breakdown, grouped/expanded client-side by the group-by
 * pills (default: Department). Group headers show spend-weighted roll-ups aligned
 * under each column; clicking a group or person row drills the trends to that
 * node (with its full ancestor path).
 */
export default function AdoptionDriverGrid({ rows, onDrill }: AdoptionDriverGridProps): ReactElement {
    const controller = useMegaGridState({
        columns: COLUMNS,
        persistenceKey: 'adoption-breakdown',
        defaultDensity: 'comfortable',
        defaultGroupBy: ['department'],
        allowMultiSort: true,
        seedViews: ADOPTION_SEED_VIEWS,
    });

    const ancestorPath = (row: AdoptionBreakdownRow): AdoptionScope[] =>
        controller.state.groupBy.map((g) => {
            const value = row.dims[g.columnKey] ?? '—';
            return { dimension: g.columnKey, value, label: value };
        });

    const handleGroupClick = (_label: string, depth: number, groupRows: AdoptionBreakdownRow[]): void => {
        const rep = groupRows[0];
        if (rep === undefined) return;
        const path = ancestorPath(rep).slice(0, depth + 1);
        onDrill(path);
    };

    const handleRowClick = (row: AdoptionBreakdownRow): void => {
        onDrill([...ancestorPath(row), { dimension: 'user', value: row.id, label: row.label }]);
    };

    return (
        <MegaGrid
            columns={COLUMNS}
            rows={rows}
            controller={controller}
            getRowKey={(r) => r.id}
            groupByPills={ADOPTION_GROUP_PILLS}
            onRowClick={handleRowClick}
            onGroupClick={handleGroupClick}
            searchAccessor={(r) => r.label}
            searchPlaceholder="Search people"
            striped
            minWidth={880}
            enableExport
            exportName="adoption-breakdown"
        />
    );
}
